import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, assetUrl } from "@/lib/api";
import { OUTPUT_META } from "@/lib/constants";
import { Navbar } from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Mic, Clapperboard, Megaphone, BookOpen, Film, Rocket, Library,
  Trash2, Globe, Loader2, ArrowRight, Sparkles,
} from "lucide-react";
import { toast } from "sonner";

const ICONS = { Clapperboard, Megaphone, BookOpen };

const StatBlock = ({ icon: Icon, value, label, color }) => (
  <div className="glass rounded-xl p-5 flex items-center gap-4">
    <div className="h-12 w-12 rounded-lg flex items-center justify-center" style={{ background: `${color}1a`, color }}>
      <Icon className="h-6 w-6" />
    </div>
    <div>
      <div className="text-2xl font-bold font-display">{value}</div>
      <div className="text-xs text-[#94A3B8]">{label}</div>
    </div>
  </div>
);

const FILTERS = [
  { key: "all", label: "All Projects" },
  { key: "movie", label: "Movies" },
  { key: "campaign", label: "Campaigns" },
  { key: "storybook", label: "Storybooks" },
];

export default function Dashboard() {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  const load = () => api.listProjects().then((d) => { setProjects(d); setLoading(false); });

  useEffect(() => {
    load();
    const t = setInterval(() => {
      api.listProjects().then(setProjects);
    }, 5000);
    return () => clearInterval(t);
  }, []);

  const remove = async (e, id) => {
    e.stopPropagation();
    await api.deleteProject(id);
    toast.success("Project deleted");
    load();
  };

  const filtered = projects.filter((p) => filter === "all" || (p.outputs || []).includes(filter));
  const count = (k) => projects.filter((p) => (p.outputs || []).includes(k)).length;

  return (
    <div className="min-h-screen">
      <Navbar />

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_rgba(59,130,246,0.12),_transparent_60%)]" />
        <div className="absolute -top-20 -right-20 h-72 w-72 rounded-full bg-[#22C55E]/10 blur-3xl" />
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8 pt-16 pb-12">
          <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-1.5 text-xs text-[#94A3B8] mb-6 fade-up">
            <Sparkles className="h-3.5 w-3.5 text-[#22C55E]" />
            Voice-first multimodal creative foundry
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight max-w-4xl leading-[1.05] fade-up">
            Speak One Idea.{" "}
            <span className="bg-gradient-to-r from-[#22C55E] to-[#3B82F6] bg-clip-text text-transparent">
              Generate Movies, Campaigns, and Stories
            </span>{" "}
            in Any Language.
          </h1>
          <p className="mt-6 text-base sm:text-lg text-[#94A3B8] max-w-2xl fade-up">
            Dictate a raw idea in English, Tamil, Hindi — even mixed Tanglish or Hinglish — and watch three fully produced creative pipelines come to life.
          </p>
          <div className="mt-8 flex flex-wrap gap-3 fade-up">
            <Button
              data-testid="hero-dictate-btn"
              onClick={() => navigate("/create")}
              className="bg-[#22C55E] hover:bg-[#16A34A] text-[#0F1117] font-semibold rounded-full h-12 px-7 text-base transition-all hover:scale-[1.03] pulse-glow"
            >
              <Mic className="h-5 w-5 mr-2" /> Dictate New Project with Voice
            </Button>
          </div>

          <div className="mt-12 grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-3xl fade-up">
            <StatBlock icon={Film} value={count("movie")} label="Active Movies" color="#3B82F6" />
            <StatBlock icon={Rocket} value={count("campaign")} label="Live Campaigns" color="#22C55E" />
            <StatBlock icon={Library} value={count("storybook")} label="Published Storybooks" color="#A855F7" />
          </div>
        </div>
      </section>

      {/* Gallery */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 pb-28">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
          <h2 className="font-display text-2xl sm:text-3xl font-semibold">Project Gallery</h2>
          <div className="flex flex-wrap gap-2">
            {FILTERS.map((f) => (
              <button
                key={f.key}
                data-testid={`filter-${f.key}`}
                onClick={() => setFilter(f.key)}
                className={`rounded-full px-4 py-1.5 text-sm transition-all ${
                  filter === f.key
                    ? "bg-[#3B82F6] text-white font-medium"
                    : "bg-white/5 text-[#94A3B8] hover:bg-white/10 hover:text-[#F8FAFC]"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-64 rounded-xl shimmer" />
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div className="glass rounded-2xl py-20 text-center">
            <Mic className="h-10 w-10 mx-auto text-[#22C55E] mb-4" />
            <p className="text-[#94A3B8]">No projects yet. Dictate your first idea to begin.</p>
            <Button
              data-testid="empty-create-btn"
              onClick={() => navigate("/create")}
              className="mt-5 bg-[#22C55E] hover:bg-[#16A34A] text-[#0F1117] font-semibold rounded-full"
            >
              <Mic className="h-4 w-4 mr-2" /> Dictate New Project
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map((p) => (
              <div
                key={p.id}
                data-testid="project-card"
                onClick={() => navigate(`/project/${p.id}`)}
                className="group cursor-pointer rounded-xl overflow-hidden bg-[#1E293B]/60 border border-white/10 hover:border-[#3B82F6]/50 transition-all hover:-translate-y-1 hover:shadow-[0_12px_40px_rgba(0,0,0,0.4)]"
              >
                <div className="relative h-40 overflow-hidden bg-[#0F1117]">
                  {p.cover_asset_id ? (
                    <img src={assetUrl(p.cover_asset_id)} alt={p.title} className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  ) : (
                    <div className="h-full w-full flex items-center justify-center bg-gradient-to-br from-[#1E293B] to-[#0F1117]">
                      {p.status === "processing" ? (
                        <div className="flex flex-col items-center gap-2 text-[#94A3B8]">
                          <Loader2 className="h-6 w-6 animate-spin text-[#22C55E]" />
                          <span className="text-xs">{p.progress?.stage || "Generating…"}</span>
                        </div>
                      ) : (
                        <Sparkles className="h-8 w-8 text-[#334155]" />
                      )}
                    </div>
                  )}
                  {p.status === "processing" && (
                    <div className="absolute bottom-0 left-0 h-1 bg-[#22C55E] transition-all" style={{ width: `${p.progress?.percent || 0}%` }} />
                  )}
                  <button
                    data-testid="delete-project-btn"
                    onClick={(e) => remove(e, p.id)}
                    className="absolute top-2 right-2 h-8 w-8 rounded-full bg-black/50 backdrop-blur flex items-center justify-center text-[#94A3B8] hover:text-red-400 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>

                <div className="p-5">
                  <div className="flex items-center justify-between gap-2 mb-2">
                    <Badge className="bg-white/5 text-[#94A3B8] border-0 gap-1 font-normal">
                      <Globe className="h-3 w-3" /> {p.output_language}
                    </Badge>
                    {p.status === "ready" ? (
                      <span className="text-[10px] text-[#22C55E]">● Ready</span>
                    ) : p.status === "error" ? (
                      <span className="text-[10px] text-red-400">● Failed</span>
                    ) : (
                      <span className="text-[10px] text-[#94A3B8]">● Processing</span>
                    )}
                  </div>
                  <h3 className="font-display text-lg font-semibold line-clamp-1 group-hover:text-[#22C55E] transition-colors">
                    {p.title}
                  </h3>
                  <div className="mt-3 flex flex-wrap gap-1.5">
                    {(p.outputs || []).map((o) => {
                      const m = OUTPUT_META[o];
                      if (!m) return null;
                      const Icon = ICONS[m.icon];
                      return (
                        <span key={o} className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-[11px]" style={{ background: `${m.color}1a`, color: m.color }}>
                          <Icon className="h-3 w-3" /> {m.label}
                        </span>
                      );
                    })}
                  </div>
                  <div className="mt-4 flex items-center justify-between text-xs text-[#64748B]">
                    <span>{(p.created_at || "").slice(0, 10)}</span>
                    <span className="inline-flex items-center gap-1 text-[#3B82F6] opacity-0 group-hover:opacity-100 transition-opacity">
                      Open <ArrowRight className="h-3 w-3" />
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
